import { formatUnits, isAddress } from 'viem'

import { IAddress } from '~/types'

// Satori (next/og) can't resolve CSS variables, so the lightness is a literal
// tuned for the dark opengraph card background.
const HSL_LIGHTNESS = '64%'

// Used when there's no address to derive a hue from.
export const FALLBACK_ACCOUNT_COLOR = '#CB5CF2'

export function getHslColor(address: IAddress, jump: number) {
  const hue = BigInt(address) % 360n
  const jumpedAlpha = ((99 - jump) % 100) / 100.0
  return `hsla(${hue}, 88%, ${HSL_LIGHTNESS}, ${jumpedAlpha})`
}

// Route params are free-form strings, and getHslColor throws on anything
// BigInt can't parse. Checksum casing is irrelevant to the hue, so shape is
// all we check.
export function getAccountColor(account: string) {
  return isAddress(account, { strict: false })
    ? getHslColor(account, 0)
    : FALLBACK_ACCOUNT_COLOR
}

/**
 * Displays a bigint using locale-aware formatting. Use displayDecimals: 'full'
 * to preserve every base unit without converting through a JavaScript number.
 * @param amount
 * @param displayDecimals
 * @param tokenDecimals
 * @param minDisplayDecimals
 * @returns A string representing the amount
 */
export const displayBigNumber = (
  amount: bigint,
  options: {
    displayDecimals?: number | 'full'
    locale?: Intl.LocalesArgument
    signDisplay?: Intl.NumberFormatOptions['signDisplay']
    tokenDecimals?: number
    minDisplayDecimals?: number
  } = {},
): string => {
  const {
    displayDecimals = 3,
    tokenDecimals = 18,
    minDisplayDecimals,
    locale,
    signDisplay,
  } = options

  const fullPrecision = displayDecimals === 'full'
  // formatUnits returns a decimal numeric string; Intl preserves its exact value.
  const value = formatUnits(amount, tokenDecimals) as `${number}`
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits:
      minDisplayDecimals ?? (fullPrecision ? 0 : displayDecimals),
    maximumFractionDigits: fullPrecision ? tokenDecimals : displayDecimals,
    signDisplay,
  }).format(fullPrecision ? value : parseFloat(value))
}

export const displayCurrency = (
  amount: number,
  options: {
    decimalPoints?: number
    showLessThanForSmallValues?: boolean
  } = {},
) => {
  const { decimalPoints = 2, showLessThanForSmallValues = false } = options
  const minimumValue = Math.pow(10, -decimalPoints)

  // If amount is positive but too small to display, show "<$X.XX"
  if (showLessThanForSmallValues && amount > 0 && amount < minimumValue) {
    return `<$${minimumValue.toFixed(decimalPoints)}`
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimalPoints,
    maximumFractionDigits: decimalPoints,
  }).format(amount)
}

export const displayCurrencyShorthand = (
  amount: number,
  decimalPoints = 1,
): string => {
  if (amount > 1e9) {
    return `${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: decimalPoints,
    }).format(amount / 1e9)}B`
  }
  if (amount > 1e6) {
    return `${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: decimalPoints,
    }).format(amount / 1e6)}M`
  }
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

  return amount >= 100 ? formattedAmount.split('.')[0] : formattedAmount
}

export function capitalizeFirstLetter(input: string): string {
  if (!input) return input
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase()
}

export function plural(word: string, value: number): string {
  return value === 1 ? word : word + 's'
}

export function formatDate(
  dateInput: string | Date | number,
  args: { short?: boolean } = {},
): string {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)

  if (args.short) {
    const now = new Date()
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      ...(now.getFullYear() !== date.getFullYear() && { year: 'numeric' }),
    }
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }

  return new Intl.DateTimeFormat('en-US', options).format(date)
}
